import React, { useState } from 'react';
import commsCheckInitialData, {
  CommsCheckContext,
  StandAloneCommsCheckScreenContext,
  StandAloneCommsCheckScreenType,
  useCommsCheckInputsContext,
} from '../../context/CommsCheckContext';
import {
  buildInitialCommsCheckMessage,
  CommsCheck,
  determineCommsCheckResultForReporting,
  filterDevicesForEsmeGsme,
} from '../commsCheck/CommsCheck';
import { ButtonContainer } from '../common/buttonContainer/ButtonContainer';
import { InlineButtonContainer } from '../common/inlineButtonContainer/InlineButtonContainer';
import { Button, ButtonTheme } from '../common/button/Button';
import { useChatReasonContext, useHomePageContext } from '../../context/HomePageContext';
import { CommsCheckResult } from '../../types/comms-check';
import { getAuthTokenOrLogOut, getMpxnError, getMsnError, isMpxnValid, isMsnValid } from '../util';
import { startChatSession } from '../chat/startChatSession';
import { TextInput } from '../common/textInput/TextInput';
import { reportingClient } from '../../api/atomProxyClient';

export default function StandAloneCommsCheck() {
  const [screen, setScreen] = React.useState<StandAloneCommsCheckScreenType>(StandAloneCommsCheckScreenType.INITIAL_QUESTIONS);
  const { commsCheckInputs, setCommsCheckInputs } = useCommsCheckInputsContext();
  const { homePageState, setHomePageState } = useHomePageContext();
  const { chatReason } = useChatReasonContext();
  const [commsCheckResultContext, setCommsCheckResultContext] = useState<CommsCheckResult>(commsCheckInitialData);
  const [showMpxnError, setShowMpxnError] = useState(false);
  const [mpxnErrorMessage, setMpxnErrorMessage] = useState('');
  const [showMsnError, setShowMsnError] = useState(false);
  const [msnErrorMessage, setMsnErrorMessage] = useState('');
  const [commsCheckStartTimeStamp, setCommsCheckStartTimeStamp] = useState<Date>(new Date('2025-01-01'));
  const [commsCheckRetriesCount, setCommsCheckRetriesCount] = useState<number>(0);

  const filteredDevicesList = filterDevicesForEsmeGsme(commsCheckResultContext.xrinv.data);
  const isMetersActive = filteredDevicesList.every((device) => device.activeStatus === true);

  async function reportCommsCheck(commsCheckResult: CommsCheckResult) {
    const commsCheckResultTimeStamp = new Date();
    const userDetails = await getAuthTokenOrLogOut();
    const requestBody = {
      mpxn: commsCheckInputs.mpxn,
      msn: commsCheckInputs.msn,
      commsCheckResultStatus: determineCommsCheckResultForReporting(commsCheckResult, isMetersActive),
      commsCheckStartingTimeStamp: commsCheckStartTimeStamp.toISOString(),
      commsCheckResultTimeStamp: commsCheckResultTimeStamp.toISOString(),
      commsCheckTimeTaken: commsCheckResultTimeStamp.getTime() - commsCheckStartTimeStamp.getTime(),
      contactReason: chatReason,
      engineerEmail: userDetails.userEmail ?? '',
    };

    // TODO: Report to sentry if any error
    reportingClient.commsCheck(requestBody).then();
  }

  function startCommsCheckHandler() {
    setHomePageState({
      ...homePageState,
      automationInProgress: true,
    });
    setCommsCheckStartTimeStamp(new Date());
    setScreen(StandAloneCommsCheckScreenType.COMMS_CHECK);
  }

  function goBackHandler() {
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setScreen(StandAloneCommsCheckScreenType.INITIAL_QUESTIONS);
  }

  function retryCommsCheckHandler() {
    setCommsCheckStartTimeStamp(new Date());
    setCommsCheckResultContext(commsCheckInitialData);
    setCommsCheckRetriesCount(commsCheckRetriesCount + 1);
  }

  function handleMpxnChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCommsCheckInputs({
      ...commsCheckInputs,
      mpxn: e.target.value,
    });
    if (!isMpxnValid(e.target.value)) {
      setShowMpxnError(true);
      setMpxnErrorMessage(getMpxnError(e.target.value));
      return;
    } else {
      setShowMpxnError(false);
      return;
    }
  }

  function handleMsnChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCommsCheckInputs({
      ...commsCheckInputs,
      msn: e.target.value,
    });
    if (!isMsnValid(e.target.value)) {
      setShowMsnError(true);
      setMsnErrorMessage(getMsnError(e.target.value));
      return;
    } else {
      setShowMsnError(false);
      return;
    }
  }

  function validCommsCheckAnswers(): boolean {
    if (showMpxnError || commsCheckInputs.mpxn == '') {
      return false;
    }
    if (showMsnError || commsCheckInputs.msn == '') {
      return false;
    }
    return true;
  }

  function renderRetryInputBoxes(data: CommsCheckResult) {
    if (data.xrinv.state && data.xritm.state && data.xrdlg.state) {
      if (
        data.xrinv.state !== 'SUCCESS' ||
        data.xritm.state !== 'SUCCESS' ||
        data.xrdlg.state !== 'SUCCESS' ||
        data.xrdlg.data.length > 0 ||
        !isMetersActive
      ) {
        return (
          <>
            <TextInput
              label="What's your MPxN?"
              inputName={'mpxnInput smallInput'}
              showError={showMpxnError}
              errorMessage={mpxnErrorMessage}
              inputValue={commsCheckInputs.mpxn}
              onChangeHandler={handleMpxnChange}
            />
            <div className="m-b-8">
              <TextInput
                label="What's your MSN?"
                inputName={'msnInput smallInput'}
                showError={showMsnError}
                errorMessage={msnErrorMessage}
                inputValue={commsCheckInputs.msn}
                onChangeHandler={handleMsnChange}
              />
            </div>
          </>
        );
      }
    }
  }

  function renderActionButtons(data: CommsCheckResult) {
    const buildInitialChatMessage = buildInitialCommsCheckMessage(
      commsCheckResultContext,
      commsCheckInputs.mpxn,
      commsCheckInputs.msn,
      chatReason,
    );
    const startChatHandler = () =>
      startChatSession({ message: buildInitialChatMessage, mpxn: commsCheckInputs.mpxn, chatReason });

    if (data.xrinv.state && data.xritm.state && data.xrdlg.state) {
      if (data.xrinv.state === 'SUCCESS' && data.xritm.state === 'SUCCESS' && data.xrdlg.state === 'SUCCESS') {
        return (
          <InlineButtonContainer>
            {data.xrdlg.data.length ? (
              <Button
                buttonId={'start-chat-comms-check'}
                buttonText={'Start Chat'}
                buttonTheme={ButtonTheme.PRIMARY}
                onClickHandler={startChatHandler}
              />
            ) : null}
            <Button
              buttonId={'retry-comms-check'}
              buttonText={'Retry comms check'}
              buttonTheme={data.xrdlg.data.length ? ButtonTheme.SECONDARY : ButtonTheme.PRIMARY}
              onClickHandler={() => {
                retryCommsCheckHandler();
              }}
            />
            <Button
              buttonId={'go-back-comms-check'}
              buttonText={'Ask about something else'}
              buttonTheme={ButtonTheme.SECONDARY}
              onClickHandler={goBackHandler}
            />
          </InlineButtonContainer>
        );
      } else {
        return (
          <InlineButtonContainer>
            <Button
              buttonId={'retry-comms-check'}
              buttonText={'Retry comms check'}
              buttonTheme={ButtonTheme.PRIMARY}
              onClickHandler={() => {
                retryCommsCheckHandler();
              }}
            />
            <Button
              buttonId={'start-chat-comms-check'}
              buttonText={'Start Chat'}
              buttonTheme={ButtonTheme.PRIMARY}
              onClickHandler={startChatHandler}
            />
            <Button
              buttonId={'go-back-comms-check'}
              buttonText={'Go Back'}
              buttonTheme={ButtonTheme.SECONDARY}
              onClickHandler={goBackHandler}
            />
          </InlineButtonContainer>
        );
      }
    } else {
      if (homePageState.automationInProgress) {
        return (
          <>
            <InlineButtonContainer>
              <Button
                buttonId="go-back-comms-check"
                buttonText="Go Back"
                buttonTheme={ButtonTheme.PRIMARY}
                data-testid={'go-back-comms-check'}
                onClickHandler={goBackHandler}
              />
            </InlineButtonContainer>
          </>
        );
      } else {
        return <></>;
      }
    }
  }

  return (
    <StandAloneCommsCheckScreenContext.Provider value={{ screen, setScreen }}>
      {screen === StandAloneCommsCheckScreenType.INITIAL_QUESTIONS && (
        <>
          <div id="commsCheckContainer" data-testid="comms-check-c">
            <TextInput
              label="What's your MPxN?"
              inputName={'mpxnInput'}
              showError={showMpxnError}
              errorMessage={mpxnErrorMessage}
              inputValue={commsCheckInputs.mpxn}
              onChangeHandler={handleMpxnChange}
            />
            <TextInput
              label="What's your MSN?"
              inputName={'msnInput'}
              showError={showMsnError}
              errorMessage={msnErrorMessage}
              inputValue={commsCheckInputs.msn}
              onChangeHandler={handleMsnChange}
            />
            <div className="information">
              <p>No MSN? Please Select "Checking site details"</p>
            </div>
          </div>
          <ButtonContainer>
            <Button
              buttonId="start-comms-check"
              buttonText="Start Comms Check"
              buttonTheme={ButtonTheme.PRIMARY}
              isButtonEnabled={validCommsCheckAnswers()}
              onClickHandler={startCommsCheckHandler}
            />
          </ButtonContainer>
        </>
      )}
      <CommsCheckContext.Provider value={{ commsCheckResultContext, setCommsCheckResultContext }}>
        {screen === StandAloneCommsCheckScreenType.COMMS_CHECK && (
          <div className="commsCheckDecommFlexContainer">
            <div className="commsCheckDecommContent">
              {renderRetryInputBoxes(commsCheckResultContext)}
              <CommsCheck
                mpxn={commsCheckInputs.mpxn}
                msn={commsCheckInputs.msn}
                retryCommsCheck={commsCheckRetriesCount}
                reportCommsCheck={reportCommsCheck}
              />
            </div>
            {renderActionButtons(commsCheckResultContext)}
          </div>
        )}
      </CommsCheckContext.Provider>
    </StandAloneCommsCheckScreenContext.Provider>
  );
}
